import FlagIcon from "@material-ui/icons/Flag";
import AppConfirmDialog from "components/Dialog/AppConfirmDialog";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, CircularProgress } from "@material-ui/core";
import { Pagination } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";
import moment from "moment";
import {
  fetchProcessNoteSummary, fetchProcessNotePage, fetchProcessNoteAnswers,
  requestNoteResubmission,
} from "store/slices/processNote";
import AppColor from "utilities/AppColor";
import AppSelect from "components/Select/AppSelect";
import InputSearch from "components/Input/InputSearch";
import { fetchWorkerListPaging } from "store/slices/worker";
import { getAuthInfo } from "store/slices/auth";
import ProgressNoteAnswersList from "./ProgressNoteAnswersList";

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles(() => ({
  root: { padding: "0 0 32px" },

  // Week navigation bar
  weekNav: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  weekNavTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: "#111827",
  },
  weekNavRight: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  weekLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: "#374151",
    minWidth: 160,
    textAlign: "center",
  },
  navBtn: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    border: "1px solid #E5E7EB",
    background: "#fff",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#374151",
    "&:hover": { background: "#F3F4F6" },
  },
  thisWeekBtn: {
    fontSize: 12,
    fontWeight: 600,
    color: AppColor.mainColor,
    border: `1px solid ${AppColor.mainColor}`,
    borderRadius: 20,
    padding: "3px 12px",
    background: "#fff",
    cursor: "pointer",
    "&:hover": { background: "#F0FDFB" },
  },

  // Day tabs
  dayTabs: {
    display: "flex",
    gap: 6,
    marginBottom: 16,
    flexWrap: "wrap",
  },
  dayTab: {
    padding: "2px 10px",
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 500,
    cursor: "pointer",
    border: "1px solid #E5E7EB",
    background: "#fff",
    color: "#374151",
    display: "flex",
    alignItems: "center",
    gap: 6,
    whiteSpace: "nowrap",
    "&:hover": { borderColor: AppColor.mainColor, color: AppColor.mainColor },
  },
  dayTabActive: {
    background: AppColor.mainColor,
    color: "#fff",
    border: `1px solid ${AppColor.mainColor}`,
    fontWeight: 700,
    "&:hover": { borderColor: AppColor.mainColor, color: "#e4e2e2" },
  },
  flagDot: {
    color: "#EF4444",
    fontSize: 12,
  },

  // Search + filters
  filterRow: {
    display: "flex",
    gap: 10,
    marginBottom: 16,
    alignItems: "center",
    flexWrap: "wrap",
  },

  filterSelect: { minWidth: 140 },
  clearBtn: {
    fontSize: 12,
    color: "#6B7280",
    cursor: "pointer",
    whiteSpace: "nowrap",
    "&:hover": { color: "#111827" },
  },

  // Summary stats
  statsRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr 1fr",
    gap: 1,
    background: "#E5E7EB",
    border: "1px solid #E5E7EB",
    borderRadius: 8,
    overflow: "hidden",
    marginBottom: 20,
  },
  statBox: {
    background: "#fff",
    padding: "14px 16px",
    textAlign: "center",
  },
  statNumber: {
    fontSize: 22,
    fontWeight: 700,
    color: AppColor.mainColor,
  },
  statNumberRed: {
    fontSize: 22,
    fontWeight: 700,
    color: "#EF4444",
  },
  statLabel: {
    fontSize: 11,
    color: "#6B7280",
    marginTop: 2,
  },

  // Note card
  noteCard: {
    // paddingBottom: "40px",
    border: "1px solid #E5E7EB",
    borderRadius: 10,
    background: "#fff",
    marginBottom: 20,
    padding: "14px 16px",
    position: "relative",
    boxShadow: "#00000026 1.95px 1.95px 2.6px",
  },
  noteCardFlagged: {
    borderLeft: "3px solid #EF4444",
    backgroundColor: "#FFF5F5",
  },
  noteCardNormal: {
    borderLeft: "3px solid #2dd4bf",
  },
  noteHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 6,
  },
  workerRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: AppColor.mainColor,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: 13,
    flexShrink: 0,
  },
  workerName: {
    fontSize: 13,
    fontWeight: 700,
    color: "#111827",
  },
  shiftLabel: {
    fontSize: 11,
    color: "#6B7280",
    marginTop: 1,
  },
  noteDateTime: {
    fontSize: 11,
    color: "#9CA3AF",
    whiteSpace: "nowrap",
    textAlign: "right",
  },

  // Flag banner
  flagBanner: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "#FEF2F2",
    border: "1px solid #FECACA",
    borderRadius: 6,
    padding: "6px 12px",
    marginBottom: 8,
    fontSize: 12,
    color: "#B91C1C",
  },

  // Note text
  noteText: {
    fontSize: 13,
    color: "#374151",
    lineHeight: 1.6,
    marginBottom: 8,
  },

  // Tags row
  tagsRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    // flexWrap: "wrap",
    gap: 6,
    marginTop: 4,
  },
  tags: {
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
  },
  tag: {
    padding: "2px 10px",
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 500,
    background: "#F3F4F6",
    color: "#374151",
  },
  tagGood:     { background: "#D1FAE5", color: "#065F46", border: "1px solid #07dea1" },
  tagAccept:   { background: "#DBEAFE", color: "#1E40AF", border: "1px solid #254dcf" },
  tagReview:   { background: "#FEF3C7", color: "#92400E", border: "1px solid #be7413" },
  tagResubmit: { background: "#FEE2E2", color: "#991B1B", border: "1px solid #e46363" },
  tagFlagged:  { background: "#FEE2E2", color: "#991B1B", border: "1px solid #e46363" },
  
  categoryCoreSupport: {
    background: "#F0FDFA",
    color: "#0F766E",
    border: "1px solid #14b8a6"
  },
  categorySocialCommunity: {
    background: "#EFF6FF",
    color: "#1E40AF",
    border: "1px solid #3b82f6"
  },
  categoryCapacityBuilding: {
    background: "#F5F3FF",
    color: "#5B21B6",
    border: "1px solid #8b5cf6"
  },

  // Request resubmission button
  resubmitBtn: {
    fontSize: 11,
    fontWeight: 600,
    color: "#0d9488",
    border: "1px solid #2dd4bf",
    borderRadius: 6,
    padding: "3px 12px",
    background: "#ffffff",
    cursor: "pointer",
    whiteSpace: "nowrap",
    "&:hover": { background: "#f6fffe" },
  },
  resubmitBtnSent: {
    fontSize: 11,
    fontWeight: 600,
    color: "#6B7280",
    border: "1px solid #E5E7EB",
    borderRadius: 6,
    padding: "3px 12px",
    background: "#F9FAFB",
    cursor: "default",
    whiteSpace: "nowrap",
  },
    answersBox: {
    marginTop: 12,
    paddingTop: 12,
    borderTop: "1px dashed #E5E7EB",
    fontSize: 13,
  },
  answerItem: {
    marginBottom: 8,
  },
  questionText: {
    fontWeight: 600,
    color: "#374151",
    marginBottom: 2,
  },
  answerText: {
    color: "#6B7280",
  },
  seeMoreLink: {
    fontSize: 12,
    color: AppColor.mainColor,
    cursor: "pointer",
    fontWeight: 600,
    position: "absolute",
    bottom: 14,
    right: 16,
    userSelect: "none"
  },

  // Bottom
  showingRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    marginTop: 16,
    fontSize: 13,
    color: "#6B7280",
  },
  loadAllBtn: {
    fontSize: 12,
    fontWeight: 600,
    color: "#374151",
    border: "1px solid #E5E7EB",
    borderRadius: 20,
    padding: "4px 14px",
    background: "#fff",
    cursor: "pointer",
    "&:hover": { background: "#F3F4F6" },
  },
  loadingBox: {
    padding: "40px 0",
    width: "100%",
    display: "flex",
    justifyContent: "center",
    gap: 10,
    color: "#6B7280",
    fontSize: 13,
    zIndex: 100,
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#ffffff52",
  },
  emptyBox: {
    padding: "48px 0",
    textAlign: "center",
    color: "#9CA3AF",
    fontSize: 13,
  },
}));

// ─── Helpers ─────────────────────────────────────────────────────────────────

const parseQuickAnswers = (raw) => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.map((s) => String(s).trim()).filter(Boolean);
  } catch (_) { /* fall through to CSV */ }
  return raw.split(",").map((s) => s.trim()).filter(Boolean);
};

// ─── Constants ────────────────────────────────────────────────────────────────


const QUALITY_SCORES = {
  GOOD: "GOOD",
  ACCEPTABLE: "ACCEPTABLE",
  NEEDS_REVIEW: "NEEDS_REVIEW",
  RESUBMIT_REQUIRED: "RESUBMIT_REQUIRED",
};

const PAGE_SIZE = 10;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getWeekStart = (date) => moment(date).startOf("isoWeek");

const formatWeekLabel = (weekStart) => {
  const end = moment(weekStart).endOf("isoWeek");
  return `${weekStart.format("D MMM")} – ${end.format("D MMM YYYY")}`;
};

const getDaysOfWeek = (weekStart) => {
  return Array.from({ length: 7 }, (_, i) => moment(weekStart).add(i, "days"));
};

const initials = (firstName, lastName) => {
  const f = (firstName || "").charAt(0).toUpperCase();
  const l = (lastName || "").charAt(0).toUpperCase();
  return f + l || "?";
};

// ─── Component ────────────────────────────────────────────────────────────────


const ParticipantProgressNoteList = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { summary, summaryLoading, pageContent, pageLoading, pagination } = useSelector((state) => ({
    summary: state.processNote.summary,
    summaryLoading: state.processNote.summaryLoading,
    pageContent: state.processNote.content,
    pageLoading: state.processNote.isLoading,
    pagination: state.processNote.pagination
  }));
  const { t } = useTranslation();
  const { id: clientId } = useParams();

  const [weekStart, setWeekStart] = useState(getWeekStart(new Date()));
  const [activeDay, setActiveDay] = useState("all");
  const [search, setSearch] = useState("");
  const [filterWorker, setFilterWorker] = useState(null);
  const [filterShift, setFilterShift] = useState(null);
  const [filterQuality, setFilterQuality] = useState(null);
  const [page, setPage] = useState(1);
  const [expandedNotes, setExpandedNotes] = useState({});
  const [fetchedAnswers, setFetchedAnswers] = useState({});
  const [loadingAnswers, setLoadingAnswers] = useState({});
  const [resubmitting, setResubmitting] = useState({});
  const [confirmNoteId, setConfirmNoteId] = useState(null);

  const days = useMemo(() => getDaysOfWeek(weekStart), [weekStart]);

  const loadSummary = useCallback(() => {
    if (!clientId) return;
    const from_date = getWeekStart(weekStart).format("YYYY/MM/DD");
    const to_date = getWeekStart(weekStart).clone().add(6, 'days').format("YYYY/MM/DD");
    
    const params = {
      client_id: clientId,
      from_date,
      to_date,
      search: search || undefined,
      worker_ids: filterWorker ? [filterWorker.value] : undefined,
      shift_list: filterShift ? [filterShift.value] : undefined,
      quality_scores: filterQuality ? [filterQuality.value] : undefined,
    };
    
    dispatch(fetchProcessNoteSummary(params));
  }, [dispatch, clientId, weekStart, search, filterWorker, filterShift, filterQuality]);

  const loadNotes = useCallback(() => {
    if (!clientId) return;
    const from_date = activeDay === "all" ? getWeekStart(weekStart).format("YYYY/MM/DD") : moment(activeDay).format("YYYY/MM/DD");
    const to_date = activeDay === "all" ? getWeekStart(weekStart).clone().add(6, 'days').format("YYYY/MM/DD") : moment(activeDay).format("YYYY/MM/DD");
    
    const params = {
      client_id: clientId,
      from_date,
      to_date,
      search: search || undefined,
      worker_ids: filterWorker ? [filterWorker.value] : undefined,
      shift_list: filterShift ? [filterShift.value] : undefined,
      quality_scores: filterQuality ? [filterQuality.value] : undefined,
      page_number: page,
      page_size: PAGE_SIZE
    };
    
    dispatch(fetchProcessNotePage(params));
    setExpandedNotes({});
  }, [dispatch, clientId, weekStart, activeDay, search, filterWorker, filterShift, filterQuality, page]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  // ── Derived data ──────────────────────────────────────────────────────────


  const authInfo = useSelector(getAuthInfo);
  const [workerListOptions, setWorkerListOptions] = useState([]);
  const [workerPage, setWorkerPage] = useState(1);
  const [hasMoreWorkers, setHasMoreWorkers] = useState(true);
  const [isFetchingWorkers, setIsFetchingWorkers] = useState(false);

  const loadWorkers = useCallback(async (page) => {
    if (isFetchingWorkers || !hasMoreWorkers) return;
    setIsFetchingWorkers(true);
    const params = {
      page: page,
      size: 25,
      asc_sort: true,
      sort_field: "firstName",
      companyId: authInfo?.companyId,
    };
    try {
      const action = await dispatch(fetchWorkerListPaging(params));
      if (action.payload && action.payload.content) {
        const newOpts = action.payload.content.map(w => ({
          value: w.id,
          label: w.name || ""
        }));
        
        setWorkerListOptions(prev => {
          const seen = new Set(prev.map(p => p.value));
          const toAdd = newOpts.filter(o => !seen.has(o.value));
          return [...prev, ...toAdd];
        });
        
        if (action.payload.content.length < 25) {
          setHasMoreWorkers(false);
        }
      } else {
        setHasMoreWorkers(false);
      }
    } finally {
      setIsFetchingWorkers(false);
    }
  }, [dispatch, authInfo, isFetchingWorkers, hasMoreWorkers]);

  useEffect(() => {
    if (workerListOptions.length === 0 && hasMoreWorkers && !isFetchingWorkers) {
      loadWorkers(1);
    }
  }, [workerListOptions.length, hasMoreWorkers, isFetchingWorkers, loadWorkers]);

  const handleLoadMoreWorkers = () => {
    if (hasMoreWorkers && !isFetchingWorkers) {
      const nextPage = workerPage + 1;
      setWorkerPage(nextPage);
      loadWorkers(nextPage);
    }
  };

  
  const workerOptions = useMemo(() => {
    return [{ value: null, label: t("label.progressNotes.allWorkers") }, ...workerListOptions];
  }, [workerListOptions, t]);

  const shiftOptions = [
    { value: null, label: t("label.progressNotes.allShifts") },
    { value: "MORNING", label: "MORNING" },
    { value: "AFTERNOON", label: "AFTERNOON" },
    { value: "EVENING", label: "EVENING" },
    { value: "NIGHT", label: "NIGHT" }
  ];

  const qualityOptions = [
    { value: null, label: t("label.progressNotes.allQuality") },
    { value: QUALITY_SCORES.GOOD, label: t("qualityScores.GOOD") },
    { value: QUALITY_SCORES.ACCEPTABLE, label: t("qualityScores.ACCEPTABLE") },
    { value: QUALITY_SCORES.NEEDS_REVIEW, label: t("qualityScores.NEEDS_REVIEW") },
    { value: QUALITY_SCORES.RESUBMIT_REQUIRED, label: t("qualityScores.RESUBMIT_REQUIRED") },
  ];

  // ── Stats ────────────────────────────────────────────────────────────────
  const totalThisWeek = summary.totalProgressNotes || 0;
  const flaggedCount = (() => {
    if (!summary.qualityFlagsPerDay) return 0;
    return Object.values(summary.qualityFlagsPerDay).reduce((a, b) => a + b, 0);
  })();
  const goalLinkedCount = summary.totalLinkedGoalTrackers || 0;
  const workerCount = summary.totalLinkedWorkers || 0;

  // ── Day tab counts ────────────────────────────────────────────────────────

  const getDayCount = (day) => {
    const key = day.format("YYYYMMDD");
    if (!summary || !summary.qualityFlagsPerDay) return 0;
    return summary.qualityFlagsPerDay[key] || 0;
  };

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handlePrevWeek = () => {
    setWeekStart(weekStart.clone().subtract(7, "days"));
    setActiveDay("all");
    setPage(1);
  };
  
  const handleNextWeek = () => {
    setWeekStart(weekStart.clone().add(7, "days"));
    setActiveDay("all");
    setPage(1);
  };
  
  const handleThisWeek = () => {
    setWeekStart(getWeekStart(new Date()));
    setActiveDay("all");
    setPage(1);
  };

  const handleClearFilters = () => {
    setSearch("");
    setFilterWorker(null);
    setFilterShift(null);
    setFilterQuality(null);
    setActiveDay("all");
  };

  
  const handleConfirmResubmission = async () => {
    if (!confirmNoteId) return;
    const noteId = confirmNoteId;
    setConfirmNoteId(null);
    setResubmitting((prev) => ({ ...prev, [noteId]: true }));
    await dispatch(requestNoteResubmission({ id: noteId }));
    setResubmitting((prev) => ({ ...prev, [noteId]: false }));
  };

  const toggleExpand = async (noteId) => {
    setExpandedNotes((prev) => ({ ...prev, [noteId]: !prev[noteId] }));
    if (!expandedNotes[noteId] && !fetchedAnswers[noteId]) {
      setLoadingAnswers(p => ({ ...p, [noteId]: true }));
      try {
        const action = await dispatch(fetchProcessNoteAnswers({ id: noteId }));
        if (action.payload) {
          setFetchedAnswers(p => ({ ...p, [noteId]: action.payload }));
        }
      } finally {
        setLoadingAnswers(p => ({ ...p, [noteId]: false }));
      }
    }
  };

  // ── Render helpers ───────────────────────────────────────────────────────

  const renderTags = (note) => {
    const tags = [];
    const score = note.qualityScore;
    
    let scoreClass = "";
    switch (score) {
      case QUALITY_SCORES.GOOD:
        scoreClass = classes.tagGood;
        break;
      case QUALITY_SCORES.ACCEPTABLE:
        scoreClass = classes.tagAccept;
        break;
      case QUALITY_SCORES.NEEDS_REVIEW:
        scoreClass = classes.tagReview;
        break;
      case QUALITY_SCORES.RESUBMIT_REQUIRED:
        scoreClass = classes.tagResubmit;
        break;
      default:
        break;
    }
    
    tags.push(<span key="score" className={`${classes.tag} ${scoreClass}`}>{t(`qualityScores.${score}`)}</span>);


    // if (note.shiftType) {
    //   tags.push(<span key="shift" className={classes.tag}>{note.shiftType.charAt(0) + note.shiftType.slice(1).toLowerCase()}</span>);
    // }

    const getCategoryStyle = (category) => {
      switch (category) {
        case "CORE_SUPPORTS":
          return classes.categoryCoreSupport;
        case "CAPITAL_SUPPORTS":
          return classes.categorySocialCommunity;
        case "CAPACITY_BUILDING":
          return classes.categoryCapacityBuilding;
        default:
          return classes.categoryCoreSupport;
      }
    };

    if (note.goalTrackerList && note.goalTrackerList.length > 0) {
      note.goalTrackerList.forEach(gt => {
        const styleClass = getCategoryStyle(gt.category);
        tags.push(<span key={`gt-${gt.id}`} className={`${classes.tag} ${styleClass}`}>🎯 {gt.name}</span>);
      });
    }

    return tags;
  };
  const renderFlags = (note) => {
    const flags = typeof note?.qualityFlags === "string" ? note.qualityFlags.split(',') :  note.qualityFlags  || [];
    if (flags.length === 0) return null;
    return (
      <Box style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
        {
          flags.map((f, idx) => {
            return (
              <span key={`flag-${idx}`} className={`${classes.tag} ${classes.tagFlagged}`}>
                <FlagIcon style={{ marginRight: 4, width: 12, height: 12 }} /> {t(`qualityFlags.${f}`)}
              </span>
            );
          })
        }
      </Box>
    )
  };

  const renderNoteText = (note) => {
    const text = note.handover || "";
    const expanded = expandedNotes[note.id];
    return (
      <>
        <Box className={classes.noteText}>
          {!expanded ? `${text.slice(0, 200)}...` : text}
        </Box>
        <span
          className={classes.seeMoreLink}
          onClick={() => toggleExpand(note.id)}
        >
          {expanded ? t("label.progressNotes.seeLess") : t("label.progressNotes.seeMore")}
        </span>
        {expanded && (
          <ProgressNoteAnswersList
            answers={fetchedAnswers[note.id]}
            loading={loadingAnswers[note.id]}
            t={t}
          />
        )}
      </>
    );
  };

  const renderNoteCard = (note) => {
    const isFlagged = note.qualityFlags && note.qualityFlags.length > 0;
    const alreadySent = note.resubmissionRequested;
    const avatarBg = note.workerId ? AppColor.mainColor : "#6B7280";
    const shift = note.shiftType
      ? `${note.shiftType.charAt(0) + note.shiftType.slice(1).toLowerCase()} shift${note.shiftStart ? ` · ${note.shiftStart}–${note.shiftEnd}` : ""}`
      : note.jobTitle || "";

    return (
      <Box
        key={note.id}
        className={`${classes.noteCard} ${isFlagged ? classes.noteCardFlagged : classes.noteCardNormal}`}
      >
        {/* Header */}
        <Box className={classes.noteHeader}>
          <Box className={classes.workerRow}>
            {note.avatarAws3Link ? (
              <img
                src={note.avatarAws3Link}
                alt=""
                style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover" }}
              />
            ) : (
              <Box className={classes.avatar} style={{ background: avatarBg }}>
                {initials(note.firstName, note.lastName)}
              </Box>
            )}
            <Box>
              <Box className={classes.workerName}>
                {`${note.firstName || ""} ${note.lastName || ""}`.trim() || "Unknown"}
              </Box>
              {shift && <Box className={classes.shiftLabel}>{shift}</Box>}
            </Box>
          </Box>
          <Box className={classes.noteDateTime}>
            {moment(note.createdDate).format("ddd D MMM")}<br />
            {moment(note.createdDate).format("HH:mm")}
          </Box>
        </Box>

        {/* Flag banner */}
        {isFlagged && note.flagReason && (
          <Box className={classes.flagBanner}>
            <i className="fas fa-exclamation-triangle" style={{ fontSize: 12 }} />
            {note.flagReason}
          </Box>
        )}

        {/* Note text */}
        {renderNoteText(note)}

        {/* Tags + action */}
        <Box className={classes.tagsRow}>
          <Box className={classes.tags}>
            {renderTags(note)}
          </Box>
          {isFlagged && (
            alreadySent ? (
              <span className={classes.resubmitBtnSent}>
                <i className="fas fa-check" style={{ marginRight: 4 }} />
                {t("label.progressNotes.resubmissionSent")}
              </span>
            ) : (
              <button
                className={classes.resubmitBtn}
                onClick={() => setConfirmNoteId(note.id)}
                disabled={resubmitting[note.id]}
              >
                {resubmitting[note.id] ? "…" : t("label.progressNotes.requestResubmission")}
              </button>
            )
          )}
        </Box>

        {/* Quality Flags */}
        {renderFlags(note)}
      </Box>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const isLoading = (pageLoading || summaryLoading);

  return (
    <Box className={classes.root}>

      {/* Week date navigator */}
      <Box className={classes.weekNav} style={{ marginBottom: 10 }}>
        <span className={classes.weekNavTitle}>{t("label.progressNotes.previousNotes")}</span>
        <Box className={classes.weekNavRight}>
          <button className={classes.navBtn} onClick={handlePrevWeek}>
            <i className="fas fa-chevron-left" style={{ fontSize: 11 }} />
          </button>
          <span className={classes.weekLabel}>{formatWeekLabel(weekStart)}</span>
          <button className={classes.navBtn} onClick={handleNextWeek}>
            <i className="fas fa-chevron-right" style={{ fontSize: 11 }} />
          </button>
          <button className={classes.thisWeekBtn} onClick={handleThisWeek}>
            {t("label.progressNotes.thisWeek")}
          </button>
        </Box>
      </Box>
      {/* Day tabs */}
      <Box className={classes.dayTabs}>
        <span
          className={`${classes.dayTab} ${activeDay === "all" ? classes.dayTabActive : ""}`}
          onClick={() => setActiveDay("all")}
        >
          All days
        </span>
        {days.map((day) => {
          const count = getDayCount(day);
          const key = day.format("YYYY/MM/DD");
          const isActive = activeDay === key;
          return (
            <span
              key={key}
              className={`${classes.dayTab} ${isActive ? classes.dayTabActive : ""}`}
              onClick={() => { setActiveDay(key); setPage(1); }}
            >
              {day.format("ddd D MMM")}
              {count > 0 && <span className={classes.flagDot}>{` · ${count} `}<FlagIcon style={{ marginRight: 2, width: 13, height: 13, marginBottom: 2 }} /></span>}
            </span>
          );
        })}
      </Box>

      {/* Search + filters */}
      <Box className={classes.filterRow}>
        <Box style={{ minWidth: '280px', flex: 1 }}>
          <InputSearch
            onSearch={(val) => { setSearch(val); setPage(1); }}
            searchText={search}
          />
        </Box>
        <Box className={classes.filterSelect}>
          <AppSelect
            placeholder={t("label.progressNotes.allWorkers")}
            options={workerOptions}
            value={filterWorker}
            onChange={(opt) => { setFilterWorker(opt); setPage(1); }}
            onMenuScrollToBottom={handleLoadMoreWorkers}
            isClearable
          />
        </Box>
        <Box className={classes.filterSelect}>
          <AppSelect
            placeholder={t("label.progressNotes.allShifts")}
            options={shiftOptions}
            value={filterShift}
            onChange={(opt) => { setFilterShift(opt); setPage(1); }}
            isClearable
          />
        </Box>
        <Box className={classes.filterSelect}>
          <AppSelect
            placeholder={t("label.progressNotes.allQuality")}
            options={qualityOptions}
            value={filterQuality}
            onChange={(opt) => { setFilterQuality(opt); setPage(1); }}
            isClearable
          />
        </Box>
        {(search || filterWorker || filterShift || filterQuality || activeDay !== "all") && (
          <span className={classes.clearBtn} onClick={handleClearFilters}>
            ✕ Clear
          </span>
        )}
      </Box>

      {/* Stats */}
      <Box className={classes.statsRow}>
        <Box className={classes.statBox}>
          <Box className={classes.statNumber}>{totalThisWeek}</Box>
          <Box className={classes.statLabel}>{t("label.progressNotes.notesThisWeek")}</Box>
        </Box>
        <Box className={classes.statBox}>
          <Box className={flaggedCount > 0 ? classes.statNumberRed : classes.statNumber}>{flaggedCount}</Box>
          <Box className={classes.statLabel}>{t("label.progressNotes.flagged")}</Box>
        </Box>
        <Box className={classes.statBox}>
          <Box className={classes.statNumber}>{goalLinkedCount}</Box>
          <Box className={classes.statLabel}>{t("label.goal_linked")}</Box>
        </Box>
        <Box className={classes.statBox}>
          <Box className={classes.statNumber}>{workerCount}</Box>
          <Box className={classes.statLabel}>{t("label.progressNotes.workers")}</Box>
        </Box>
      </Box>

      {/* Notes list */}
      {pagination.totalElements === 0 ? (
        <Box className={classes.emptyBox} position="relative">
          {
            isLoading &&
            <Box className={classes.loadingBox}>
              <CircularProgress size={24} style={{ color: AppColor.mainColor }} />
              {t("message.loading_notes")}
            </Box>
          }
          {
            !isLoading &&
            t("label.progressNotes.noNotes")
          }
        </Box>
      ) : (
        <Box
          position="relative"
          sx={{
            '& > *:not(.loading-overlay)': {
              pointerEvents: isLoading ? 'none' : 'auto',
              transition: 'filter 0.3s ease, opacity 0.3s ease'
            }
          }}
        >
          {
            isLoading &&
            <Box className={classes.loadingBox}>
              <CircularProgress size={24} style={{ color: AppColor.mainColor }} />
              {t("message.loading_notes")}
            </Box>
          }
          {pageContent && pageContent.map((note) => renderNoteCard(note))}

          <Box display="flex" justifyContent="center" mt={2} mb={4}>
            <Pagination count={pagination.totalPages} page={page} onChange={(e, val) => setPage(val)} />
          </Box>
        </Box>
      )}
      <AppConfirmDialog
        open={Boolean(confirmNoteId)}
        onClose={() => setConfirmNoteId(null)}
        onConfirm={handleConfirmResubmission}
        title={t("label.progressNotes.requestResubmission", "Request Resubmission")}
        content={t("message.confirm.requestResubmission", "Are you sure you want to request resubmission for this progress note?")}
      />
    </Box>
  );
}; 

export default ParticipantProgressNoteList;
